import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, X } from "lucide-react";

/**
 * PWA install prompt. Appears as a small floating card bottom-right on supported
 * browsers (Chrome/Edge on desktop, Chrome/Samsung on Android). iOS Safari must
 * install manually (Share → Add to Home Screen) — we show instructions for it.
 *
 * Dismissed preference is remembered in localStorage (sc_pwa_dismissed).
 */
export default function InstallPrompt() {
  const { t, i18n } = useTranslation();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem("sc_pwa_dismissed");
    if (dismissed) return;

    // iOS Safari detection
    const ua = window.navigator.userAgent.toLowerCase();
    const iOS = /iphone|ipad|ipod/.test(ua) && !window.MSStream;
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (isStandalone) return; // already installed

    if (iOS) {
      setIsIOS(true);
      // Show iOS hint after 3s on first visit
      const tm = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(tm);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Also listen for successful install
    const installed = () => { setVisible(false); localStorage.setItem("sc_pwa_dismissed", "installed"); };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted" || outcome === "dismissed") {
      setDeferred(null);
      setVisible(false);
      localStorage.setItem("sc_pwa_dismissed", outcome);
    }
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("sc_pwa_dismissed", "user");
  };

  if (!visible) return null;

  return (
    <div className="pwa-install-card" role="dialog" aria-live="polite" data-testid="pwa-install-card">
      <button className="pwa-close" onClick={dismiss} aria-label="Close" data-testid="pwa-close">
        <X size={14} />
      </button>
      <div className="pwa-install-title">
        {t("installPrompt.install_square_compass")}
      </div>
      <p className="pwa-install-desc">
        {isIOS
          ? t("installPrompt.open_the_share_menu_and")
          : t("installPrompt.install_the_app_on_your")}
      </p>
      {!isIOS && (
        <button className="btn-primary-gold flex items-center gap-2" onClick={install} data-testid="pwa-install-btn">
          <Download size={14} /> {t("installPrompt.install")}
        </button>
      )}
    </div>
  );
}
